/**
 * ขั้นที่ 2 — สแกนเข้า (Scan-In) เปิดรอบงานที่ work center
 *
 * Scan-In เป็นการ **เปิด** `operation_confirmation` เท่านั้น ยังไม่ตัดสต๊อก
 * (D-063/D-064) — สามช่องบนหน้าจอนี้บวกโรงงานคือทั้งหมดที่ contract รับ
 *
 * ไม่ POST: ค่าที่กรอกกลายเป็นคำสั่ง `SCAN_IN` หนึ่งใบในคิวออฟไลน์ (D-075)
 *
 * ด่านของ contract ที่ยังเป็นของเซิร์ฟเวอร์ทั้งหมด: order ต้อง RELEASED/IN_PROCESS,
 * ต้องมี staged lot อยู่ก่อน, work center ต้องตรงกับ routing snapshot และห้ามมี
 * cycle ค้างเปิดอยู่ — หน้าจอไม่ทำซ้ำเพราะจะกลายเป็นความจริงชุดที่สองที่ตัดสินจาก
 * ข้อมูลที่เครื่องหน้างานไม่มี
 */

import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { MachineScanPanel, type ScannedMachine } from './MachineScanPanel';
import { ScanCommandForm, ScanField, type ScanScreenProps } from './ScanCommandForm';
import { useScanSubmit, type ScanCommandDraft } from './useScanSubmit';
import { SCAN_IN } from '../lib/shopFloor/commands';
import { isPositiveInteger } from '../lib/shopFloor/quantities';

export function ScanInScreen({ pendingSync }: ScanScreenProps) {
  /*
   * ค่าเริ่มต้นจาก TagScanScreen (`?order=<uuid>&seq=<n>`, D-170) — อ่านครั้งแรก
   * ที่ mount เท่านั้น ผู้ใช้แก้ไขต่อได้ตามปกติ ไม่มี param = พฤติกรรมเดิมทุกประการ
   */
  const [searchParams] = useSearchParams();
  const [plantCode, setPlantCode] = useState('');
  const [productionOrderId, setProductionOrderId] = useState(() => searchParams.get('order') ?? '');
  const [operationSeq, setOperationSeq] = useState(() => searchParams.get('seq') ?? '');
  const [workCenterCode, setWorkCenterCode] = useState('');
  const [machines, setMachines] = useState<readonly ScannedMachine[]>([]);
  const { state, submit } = useScanSubmit();

  const buildDraft = (): ScanCommandDraft => {
    const problems: string[] = [];
    const plant = plantCode.trim();
    const order = productionOrderId.trim();
    const seq = operationSeq.trim();
    const workCenter = workCenterCode.trim();

    if (plant === '') {
      problems.push('ต้องระบุรหัสโรงงาน — ทุกคำสั่งเป็นของโรงงานใดโรงงานหนึ่งเสมอ (D-004)');
    }
    if (order === '') {
      problems.push('ต้องระบุใบสั่งผลิตที่จะเปิดรอบงาน');
    }
    if (!isPositiveInteger(seq)) {
      problems.push('ลำดับขั้นตอน (operation_seq) ต้องเป็นจำนวนเต็มมากกว่า 0');
    }
    if (workCenter === '') {
      problems.push('ต้องระบุ work center ที่กำลังเปิดงาน');
    }

    // Whether a machine scan is required is the server's switch (shop_floor_machine_scan_required, off until every
    // machine has its QR label): with none scanned the command is sent without machine_qr, and a plant that has
    // turned the switch on answers MACHINE_SCAN_REQUIRED.

    if (problems.length > 0) {
      return { ok: false, problems };
    }

    return {
      ok: true,
      command: {
        command_type: SCAN_IN,
        payload: {
          plant_code: plant,
          production_order_id: order,
          operation_seq: Number(seq),
          work_center_code: workCenter,
          ...(machines.length === 0 ? {} : { machine_qr: machines.map((machine) => machine.raw) }),
        },
      },
    };
  };

  const handleSubmit = async () => {
    const result = await submit(buildDraft());
    if (result?.status === 'queued') {
      /* ใบสั่งผลิตถัดไปเป็นคนละใบเสมอ แต่เครื่องยังอยู่ที่ work center เดิม */
      setProductionOrderId('');
      setOperationSeq('');
      setMachines([]);
    }
  };

  return (
    <ScanCommandForm
      testId="scan-in-screen"
      title="เปิดรอบงานที่ work center"
      hint="สแกนป้าย Kanban แล้วสแกน QR ที่เครื่อง จากนั้นกดปุ่มเริ่มผลิต — เปิดรอบงานเท่านั้น ยังไม่ตัดสต๊อก ต้องจัดเตรียมวัตถุดิบเข้าจุด STAGING มาก่อนแล้ว"
      submitLabel="เริ่มผลิต"
      state={state}
      pendingSync={pendingSync}
      onSubmit={() => {
        void handleSubmit();
      }}
    >
      <ScanField
        label="รหัสโรงงาน (plant_code)"
        name="plant_code"
        testId="scan-in-plant"
        value={plantCode}
        onChange={setPlantCode}
        placeholder="เช่น BKK-01"
        hint="ยังต้องกรอกเองจนกว่าอุปกรณ์จะถูกลงทะเบียนผูกกับโรงงาน/ไลน์"
      />
      <ScanField
        label="ใบสั่งผลิต (production_order_id)"
        name="production_order_id"
        testId="scan-in-order"
        value={productionOrderId}
        onChange={setProductionOrderId}
      />
      <ScanField
        label="ลำดับขั้นตอน (operation_seq)"
        name="operation_seq"
        testId="scan-in-operation"
        value={operationSeq}
        onChange={setOperationSeq}
        inputMode="numeric"
        placeholder="เช่น 20"
      />
      <ScanField
        label="work center (work_center_code)"
        name="work_center_code"
        testId="scan-in-work-center"
        value={workCenterCode}
        onChange={setWorkCenterCode}
        placeholder="เช่น WC-CUT-01"
        hint="ต้องตรงกับ work center ของขั้นตอนนี้ใน routing — เซิร์ฟเวอร์เป็นผู้ตรวจตอนซิงค์"
      />
      <MachineScanPanel machines={machines} onChange={setMachines} />
    </ScanCommandForm>
  );
}
